import { decode as decodePolyline } from '@googlemaps/polyline-codec'
import { head, last, reverse } from 'ramda'
import { MapRef } from 'react-map-gl/mapbox'
import { useEffect } from 'react'
import { DirectionsRouteResponse, GeographicCoordinates, RouteStreetSegment } from '@/api/navigationApi/types.gen'

export const determineDestinationNode = (streetSegment: RouteStreetSegment): GeographicCoordinates | undefined =>
  streetSegment.direction_forward ? last(streetSegment.geom.points) : head(streetSegment.geom.points)

export const determineSourceNode = (streetSegment: RouteStreetSegment): GeographicCoordinates | undefined =>
  streetSegment.direction_forward ? head(streetSegment.geom.points) : last(streetSegment.geom.points)

export const generateDriveSegmentGeoJSON = (
  futureSegments?: RouteStreetSegment[],
  currentSegment?: RouteStreetSegment,
  getSegmentColors?: (
    index: number,
    array: Array<{
      type: 'Feature'
      geometry: {
        type: 'LineString'
        coordinates: Array<Array<number>>
      }
      properties: Record<string, unknown>
    }>,
    hasCurrentSegment: boolean
  ) => string
) => {
  const futureSegmentsGeoJson = futureSegments
    ? {
        type: 'FeatureCollection' as const,
        features: [...futureSegments]
          .reverse()
          .map(({ geom, direction_forward }) => ({
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates: (direction_forward ? geom.points : reverse(geom.points)).map(
                ({ latitude, longitude }: GeographicCoordinates) => [longitude, latitude]
              ),
            },
            properties: {},
          }))
          .map((feature, index, array) => {
            const color = getSegmentColors?.(index, array, !!currentSegment) || '#BFEE90' // fallback to light green

            return {
              ...feature,
              properties: {
                color,
              },
            }
          }),
      }
    : undefined

  const currentSegmentGeoJson = currentSegment
    ? (() => {
        const feature = {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: (currentSegment.direction_forward
              ? currentSegment.geom.points
              : reverse(currentSegment.geom.points)
            ).map(({ latitude, longitude }: GeographicCoordinates) => [longitude, latitude]),
          },
          properties: {},
        }
        return {
          ...feature,
          properties: {
            color: '#fa5252', // red-6
          },
        }
      })()
    : undefined

  return { futureSegmentsGeoJson, currentSegmentGeoJson }
}

type TechnicalSegment = {
  technical_segment_id: string
  geom: { points: GeographicCoordinates[][] }
}

export const generateTechnicalSegmentGeoJSON = (
  futureSegments?: TechnicalSegment[]
): GeoJSON.FeatureCollection<GeoJSON.Polygon> | undefined => {
  return futureSegments
    ? {
        type: 'FeatureCollection' as const,
        features: futureSegments.flatMap(segment =>
          segment.geom.points.map(points => ({
            type: 'Feature' as const,
            geometry: {
              type: 'Polygon' as const,
              coordinates: [points.map(({ latitude, longitude }) => [longitude, latitude])],
            },
            properties: {
              color: '#9775fa', // grape-5
              technicalSegmentId: segment.technical_segment_id,
            },
          }))
        ),
      }
    : undefined
}

export const generateDirectionsGeoJSON = (directionRoute?: DirectionsRouteResponse) => {
  if (!directionRoute) return undefined

  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: decodePolyline(directionRoute.encoded_polyline).map(([latitude, longitude]) => [
            longitude,
            latitude,
          ]),
        },
        properties: {
          color: '#9D00FF', // purple - distinct from route segment green
        },
      },
    ],
  }
}

export const useMapImage = (
  map: MapRef | undefined,
  name: string,
  url: string,
  dimensions?: { width: number; height: number }
): void => {
  useEffect(() => {
    if (map === undefined) return

    if (url.endsWith('.png')) {
      const { width, height } = dimensions ?? { width: undefined, height: undefined }
      const image = new Image(width, height)
      image.onload = () => {
        if (map.hasImage(name)) {
          map.removeImage(name)
        }
        map.addImage(name, image)
      }
      image.src = url
    } else {
      map.loadImage(url, (error, image) => {
        if (error) {
          throw error
        }

        if (image) {
          if (map.hasImage(name)) {
            map.removeImage(name)
          }
          map.addImage(name, image)
        }
      })
    }
    // dimensions intentionally read once on mount; complex map?.hasImage call is the actual signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, name, url, map?.hasImage(name)])
}

const MIN_SEGMENT_LENGTH_METERS = 20
const MIN_LABEL_SPACING_METERS = 40

const haversineDistanceMeters = (c1: number[], c2: number[]): number => {
  const R = 6371000
  const lat1 = (c1[1] * Math.PI) / 180
  const lat2 = (c2[1] * Math.PI) / 180
  const dLat = ((c2[1] - c1[1]) * Math.PI) / 180
  const dLon = ((c2[0] - c1[0]) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const polylineLengthMeters = (coords: number[][]): number => {
  let total = 0
  for (let i = 1; i < coords.length; i++) {
    total += haversineDistanceMeters(coords[i - 1], coords[i])
  }
  return total
}

const interpolateAlongPolyline = (
  coords: number[][],
  targetDistance: number
): { coord: number[]; segmentIndex: number } => {
  let accumulated = 0
  for (let i = 1; i < coords.length; i++) {
    const segLen = haversineDistanceMeters(coords[i - 1], coords[i])
    if (accumulated + segLen >= targetDistance) {
      const ratio = segLen === 0 ? 0 : (targetDistance - accumulated) / segLen
      return {
        coord: [
          coords[i - 1][0] + ratio * (coords[i][0] - coords[i - 1][0]),
          coords[i - 1][1] + ratio * (coords[i][1] - coords[i - 1][1]),
        ],
        segmentIndex: i - 1,
      }
    }
    accumulated += segLen
  }
  return { coord: coords[coords.length - 1], segmentIndex: Math.max(0, coords.length - 2) }
}

const findPolylineMidpoint = (coords: number[][]): number[] => {
  if (coords.length === 0) return [0, 0]
  if (coords.length === 1) return coords[0]
  return interpolateAlongPolyline(coords, polylineLengthMeters(coords) / 2).coord
}

export const contrastTextColor = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  return luminance > 0.4 ? '#333333' : '#ffffff'
}

export const getSegmentLabelStyle = (label: number, color: string): { color: string; textColor: string } => {
  if (label === 1) {
    return {
      color: '#fa5252',
      textColor: '#ffffff',
    }
  }
  return {
    color,
    textColor: contrastTextColor(color),
  }
}

export const generateSegmentLabelsGeoJSON = (
  segments: Array<{ coordinates: number[][]; color: string; label: number }>
): GeoJSON.FeatureCollection<GeoJSON.Point> => {
  const placed: number[][] = []
  const features: GeoJSON.Feature<GeoJSON.Point>[] = []

  for (const { coordinates, color, label } of segments) {
    if (polylineLengthMeters(coordinates) < MIN_SEGMENT_LENGTH_METERS) continue

    const midpoint = findPolylineMidpoint(coordinates)
    const tooClose = placed.some(p => haversineDistanceMeters(p, midpoint) < MIN_LABEL_SPACING_METERS)
    if (tooClose) continue

    placed.push(midpoint)
    const labelStyle = getSegmentLabelStyle(label, color)
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: midpoint },
      properties: {
        label: String(label),
        color: labelStyle.color,
        textColor: labelStyle.textColor,
        isCurrent: label === 1,
      },
    })
  }

  return { type: 'FeatureCollection', features }
}

export const generateDetectionsGeoJSON = (detections: Array<GeographicCoordinates>, color: string) => {
  return {
    type: 'FeatureCollection' as const,
    features: detections.map((detection, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [detection.longitude, detection.latitude],
      },
      properties: {
        index,
        color,
      },
    })),
  }
}

export const generateSignPostsGeoJSON = (signPosts: Array<GeographicCoordinates>) => {
  return {
    type: 'FeatureCollection' as const,
    features: signPosts.map((signPost, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [signPost.longitude, signPost.latitude],
      },
      properties: {
        index,
      },
    })),
  }
}
